import { useState, useEffect, useCallback } from 'react'
import { toast } from 'react-toastify'

import { PeriodOption } from '../../models/Frequency/types'
import { InspectionType, AreaConfig } from '../../models/Config/types'
import { axiosInstance } from '../../helpers'
import { Unit } from '../../models'
import { SINGLE_AREA_FACILITY_ITEMS } from '../../config/facilityItems'
import { findBestMatch } from '../../helpers/stringHelpers'
import { CreateAreaConfigRequest } from '../../store'
import {
    FacilityConfigs,
    FacilitiesState,
    FacilityItem,
    AreaInventoryConfig,
    FacilitiesPlanRequest,
} from './types'

export const useFacilitiesPlan = () => {
    const [facilitiesState, setFacilitiesState] = useState<FacilitiesState>(
        SINGLE_AREA_FACILITY_ITEMS.reduce(
            (acc, item) => ({
                ...acc,
                [item.id]: {
                    value: item.value,
                    period: item.period,
                    inspectionType: item.inspectionType,
                    selectedUnit: item.selectedUnit,
                    name: item.name,
                },
            }),
            {},
        ),
    )
    const [inspectionTypes, setInspectionTypes] = useState<InspectionType[]>([])
    const [inspectionUnits, setInspectionUnits] = useState<Unit[]>([])
    const [loadingTypes, setLoadingTypes] = useState(false)
    const [loadingUnits, setLoadingUnits] = useState(false)
    const [newAreaConfigList, setNewAreaConfigList] = useState<AreaConfig[]>([])
    const [facilityConfigs, setFacilityConfigs] = useState<FacilityConfigs>(
        DEFAULT_FACILITY_CONFIGS,
    )
    const isLoading = loadingTypes || loadingUnits

    // Get inspection types and units

    const getInspectionTypes = useCallback(
        async (apartmentId: number) => {
            try {
                setLoadingTypes(true)
                const response = await axiosInstance.get(
                    `account-manager/inspection-types/`,
                    {
                        params: {
                            apartment_id: apartmentId,
                        },
                    },
                )

                const inspectionTypes: InspectionType[] = response.data
                setInspectionTypes(inspectionTypes)

                if (inspectionUnits.length > 0) {
                    updateFacilityMatches(inspectionTypes, inspectionUnits)
                }

                return inspectionTypes
            } catch (error) {
                toast.error(`Error fetching inspection types: ${error}`)
                return Promise.reject(error)
            } finally {
                setLoadingTypes(false)
            }
        },
        [inspectionUnits],
    )

    const getInspectionUnits = useCallback(
        async (apartmentId: number) => {
            try {
                setLoadingUnits(true)
                const response = await axiosInstance.get(
                    `/account-manager/properties/${apartmentId}/units/get_inspection_units/`,
                )
                const inspectionUnits: Unit[] = response.data
                setInspectionUnits(inspectionUnits)

                if (inspectionTypes.length > 0) {
                    updateFacilityMatches(inspectionTypes, inspectionUnits)
                }

                return inspectionUnits
            } catch (error) {
                toast.error(`Error fetching inspection units: ${error}`)
                return Promise.reject(error)
            } finally {
                setLoadingUnits(false)
            }
        },
        [inspectionTypes],
    )

    const updateFacilityMatches = useCallback(
        (types: InspectionType[], units: Unit[]) => {
            const updatedConfigs = { ...facilityConfigs }
            Object.entries(FACILITY_SEARCH_TERMS).forEach(
                ([facilityType, searchTerms]) => {
                    const bestUnitMatch = findBestMatch(
                        searchTerms.join(' '),
                        units,
                        searchTerms,
                    )

                    const bestTypeMatch = findBestMatch(
                        searchTerms.join(' '),
                        types,
                        [...searchTerms, bestUnitMatch?.name || ''],
                    )

                    updatedConfigs[facilityType as keyof FacilityConfigs] = {
                        ...updatedConfigs[
                            facilityType as keyof FacilityConfigs
                        ],
                        unit: bestUnitMatch,
                        inspectionType: bestTypeMatch,
                    }
                },
            )
            setFacilityConfigs(updatedConfigs)

            setFacilitiesState((currentState) => {
                const updatedState = Object.entries(currentState).reduce(
                    (acc, [id, facility]) => {
                        const bestUnitMatch = findBestMatch(
                            facility.name,
                            units,
                            [id.replace(/_/g, ' ')],
                        )

                        const bestTypeMatch = findBestMatch(
                            facility.name,
                            types,
                            [id.replace(/_/g, ' '), bestUnitMatch?.name || ''],
                        )
                        return {
                            ...acc,
                            [id]: {
                                ...facility,
                                selectedUnit: bestUnitMatch,
                                inspectionType: bestTypeMatch,
                            },
                        }
                    },
                    {} as FacilitiesState,
                )

                return updatedState
            })
        },
        [],
    )

    useEffect(() => {
        if (inspectionTypes.length > 0 && inspectionUnits.length > 0) {
            updateFacilityMatches(inspectionTypes, inspectionUnits)
        }
    }, [inspectionTypes, inspectionUnits, updateFacilityMatches])

    // Single area facilties plan

    const facilities = useCallback((): FacilityItem[] => {
        return Object.entries(facilitiesState).map(([id, facility]) => ({
            id,
            name: facility.name,
            value: facility.value,
            period: facility.period,
            inspectionType: facility.inspectionType,
            selectedUnit: facility.selectedUnit,
        }))
    }, [facilitiesState])

    const handleFacilityChange = useCallback(
        (id: string, value: 'yes' | 'no' | null) => {
            setFacilitiesState((prev) => ({
                ...prev,
                [id]: {
                    ...prev[id],
                    value,
                    selectedUnit:
                        value === 'no' ? null : prev[id]?.selectedUnit,
                    inspectionType:
                        value === 'no' ? null : prev[id]?.inspectionType,
                },
            }))
        },
        [],
    )

    const handlePeriodChange = useCallback(
        (id: string, period: PeriodOption) => {
            setFacilitiesState((prev) => ({
                ...prev,
                [id]: {
                    ...prev[id],
                    period,
                },
            }))
        },
        [],
    )

    const handleUnitChange = useCallback(
        (id: string, selectedUnit: Unit | null) => {
            setFacilitiesState((prev) => ({
                ...prev,
                [id]: {
                    ...prev[id],
                    selectedUnit,
                    value: selectedUnit ? 'yes' : prev[id]?.value,
                },
            }))
        },
        [],
    )

    const handleInspectionTypeChange = useCallback(
        (id: string, inspectionType: InspectionType | null) => {
            setFacilitiesState((prev) => ({
                ...prev,
                [id]: {
                    ...prev[id],
                    inspectionType,
                },
            }))
        },
        [],
    )

    const resetFacilities = useCallback(() => {
        setFacilitiesState(
            SINGLE_AREA_FACILITY_ITEMS.reduce(
                (acc, item) => ({
                    ...acc,
                    [item.id]: {
                        value: item.value,
                        period: item.period,
                        inspectionType: null,
                        selectedUnit: null,
                        name: item.name,
                    },
                }),
                {},
            ),
        )
        setInspectionTypes([])
        setInspectionUnits([])
    }, [])

    const createSingleAreaFacilitiesPlan = async (apartmentId: number) => {
        const facilitiesAPI = facilities()
            .filter((facility) => facility.value === 'yes')
            .map((facility) => {
                const value = facility.value
                if (value === 'yes') {
                    return {
                        period: facility.period,
                        inspection_type: facility.inspectionType?.id,
                        unit_id: facility.selectedUnit?.id,
                    }
                }
            })

        try {
            const response = await axiosInstance.post(
                `/frequency/create_single_area_facilities_plan/`,
                {
                    apartment_id: apartmentId,
                    facilities: facilitiesAPI,
                },
            )
            toast.success('Facilities plan created successfully')
            return response.data
        } catch (error) {
            toast.error('Error creating facilities plan')
        }
    }

    // New area facilties plan

    const handleUpdateNewAreaFacilityConfig = useCallback(
        (
            facilityType: keyof FacilityConfigs,
            update: Partial<{
                unit: Unit | null
                inspectionType: InspectionType | null
                period: PeriodOption
            }>,
        ) => {
            setFacilityConfigs((prev) => ({
                ...prev,
                [facilityType]: {
                    ...prev[facilityType],
                    ...update,
                },
            }))
        },
        [],
    )

    const handleAddNewAreaConfig = useCallback(
        (
            facilityType: keyof FacilityConfigs,
            areaConfigs: AreaInventoryConfig[],
        ) => {
            setFacilityConfigs((prev) => ({
                ...prev,
                [facilityType]: {
                    ...prev[facilityType],
                    areaConfigs: areaConfigs,
                },
            }))
        },
        [],
    )

    const resetFacilityConfigs = () => {
        setFacilityConfigs(DEFAULT_FACILITY_CONFIGS)
    }

    // Reset all
    const resetAll = () => {
        resetFacilities()
        resetFacilityConfigs()
    }

    const createAreaConfig = async (
        request: CreateAreaConfigRequest,
        apartmentId: number,
    ) => {
        try {
            const response = await axiosInstance.post(
                `account-manager/properties/${apartmentId}/area-configs/get_or_create_area_config/`,
                request.body,
            )

            const areaConfig: AreaConfig = response.data
            setNewAreaConfigList((prev) => [...prev, areaConfig])

            return areaConfig
        } catch (error) {
            toast.error(`Error creating area config: ${error}`)
            return Promise.reject(error)
        }
    }

    const createNewAreaFacilitiesPlan = async (apartmentId: number) => {
        const facilitiesAPI: FacilitiesPlanRequest[] = []
        Object.entries(facilityConfigs).forEach(([key, facilityConfig]) => {
            if (facilityConfig.areaConfigs.length > 0) {
                facilitiesAPI.push({
                    unit_id: facilityConfig.unit?.id,
                    inspection_type: facilityConfig.inspectionType?.id,
                    period: facilityConfig.period,
                    area_configs: facilityConfig.areaConfigs,
                })
            }
        })
        try {
            const response = await axiosInstance.post(
                `/frequency/create_new_area_facilities_plan/`,
                {
                    apartment_id: apartmentId,
                    facilities: facilitiesAPI,
                },
            )
            toast.success('Facilities plan created successfully')
            return response.data
        } catch (error) {
            toast.error('Error creating facilities plan')
        }
    }

    return {
        facilities: facilities(),
        facilitiesState,
        inspectionTypes,
        inspectionUnits,
        isLoading,
        facilityConfigs,
        newAreaConfigList,
        handleFacilityChange,
        handlePeriodChange,
        handleUnitChange,
        handleInspectionTypeChange,
        resetFacilities,
        getInspectionTypes,
        getInspectionUnits,
        createSingleAreaFacilitiesPlan,
        resetAll,
        createAreaConfig,
        handleUpdateNewAreaFacilityConfig,
        handleAddNewAreaConfig,
        createNewAreaFacilitiesPlan,
    }
}

export const GROUNDS = 'grounds'
export const FIRE_EXT = 'fireExt'
export const FIRE_ALARM = 'fireAlarm'
export const BACKFLOW = 'backflow'
export const LIGHTS = 'lights'

const FACILITY_SEARCH_TERMS = {
    grounds: ['grounds', 'ground'],
    fireExt: ['fire extinguisher', 'extinguisher', 'fire ext'],
    fireAlarm: ['fire alarm', 'alarm system', 'fire system'],
    backflow: ['backflow', 'back flow'],
    lights: ['light audit', 'lighting audit', 'light inspection'],
}

const DEFAULT_FACILITY_CONFIGS: FacilityConfigs = {
    grounds: {
        unit: null,
        inspectionType: null,
        period: PeriodOption.WD,
        areaConfigs: [],
    },
    fireExt: {
        unit: null,
        inspectionType: null,
        period: PeriodOption.MN,
        areaConfigs: [],
    },
    fireAlarm: {
        unit: null,
        inspectionType: null,
        period: PeriodOption.MN,
        areaConfigs: [],
    },
    backflow: {
        unit: null,
        inspectionType: null,
        period: PeriodOption.MN,
        areaConfigs: [],
    },
    lights: {
        unit: null,
        inspectionType: null,
        period: PeriodOption.MN,
        areaConfigs: [],
    },
}

export const PERIOD_LABELS = {
    [PeriodOption.DY]: 'Daily',
    [PeriodOption.WK]: 'Weekly',
    [PeriodOption.BW]: 'Bi-Weekly',
    [PeriodOption.MN]: 'Monthly',
    [PeriodOption.BY]: 'Bi-Yearly',
    [PeriodOption.YR]: 'Yearly',
    [PeriodOption.WD]: 'Weekdays',
}
